import { useMemo } from 'react';
import Table from '../../table/table';
import { columns } from './variables/synced-transactions';
import { SyncedTransactionsTypes } from './types';
import IconLoader from '../../icons/loader';
import { BaseTransaction, Currency } from '../types';
import formatDate from '../../../helpers/format-date';
import formatBalance from '../../../helpers/format-balance';

import styles from '../transaction.module.css';

const formatSyncedTransactionsList = (
  list: BaseTransaction[],
  currencies?: Currency[],
): BaseTransaction[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let sellAmount;
    let buyAmount;

    const exchangeType =
      result.exchangeCurrencyFixedId === result.exchangeCurrencyFromId
        ? 'Sell'
        : 'Buy';

    if (exchangeType === 'Sell') {
      sellAmount = result?.sum;
      buyAmount = result?.exchangeCurrencyFlexibleSum;
    } else {
      sellAmount = result?.exchangeCurrencyFlexibleSum;
      buyAmount = result?.sum;
    }

    const sellCurrency = currencies?.find(
      (currency) => currency.id === result.exchangeCurrencyFromId,
    );

    const buyCurrency = currencies?.find(
      (currency) => currency.id === result.exchangeCurrencyToId,
    );

    const render: Record<string, any> = {
      transferDate: (
        <div>
          {result?.transferDate
            ? formatDate(result?.transferDate || '', true)
            : '—'}
        </div>
      ),
      exchangeCurrencyFixedId: <div>{exchangeType}</div>,
      exchangeCurrencyFromId: <div>{result?.exchangeCurrencyFrom?.name}</div>,
      exchangeCurrencyToId: <div>{result?.exchangeCurrencyTo?.name}</div>,
      sum: (
        <div>
          {sellAmount ? formatBalance(Number(sellAmount), sellCurrency) : '-'}
        </div>
      ),
      exchangeCurrencyFlexibleSum: (
        <div>
          {buyAmount ? formatBalance(Number(buyAmount), buyCurrency) : '-'}
        </div>
      ),
      senderAccountNumber: result?.senderAccount ? (
        <>
          <div>{result?.senderAccount?.description}</div>
          <div className={styles.accNumber}>
            {result?.senderAccount?.fullNumber}
          </div>
        </>
      ) : (
        result.senderAccountNumber
      ),
      recipientAccountNumber: result?.recipientAccount ? (
        <>
          <div>{result?.recipientAccount?.description}</div>
          <div className={styles.accNumber}>
            {result?.recipientAccount?.fullNumber}
          </div>
        </>
      ) : (
        result.recipientAccountNumber
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

export default function SyncedTransactions({
  syncedTransactionsIsLoading,
  syncedTransactionsIsFetching,
  syncedTransactions,
  currencies,
}: SyncedTransactionsTypes) {
  let content;

  const formattedData = useMemo(() => {
    const data = formatSyncedTransactionsList(
      syncedTransactions as BaseTransaction[],
      currencies,
    );

    return data;
  }, [syncedTransactions, currencies]);

  if (syncedTransactionsIsLoading) {
    content = (
      <div className={styles.contentLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={styles.syncedTransactionsContent}>
        <Table
          filters={{}}
          onFilterChange={() => {}}
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          sort={{}}
          columnsLabels={{}}
          isLoading={syncedTransactionsIsFetching}
          onRowClick={(id) =>
            window.open(`/transactions/exchange/${id}`, '_blank')
          }
          withScroll
        />
      </div>
    );
  }

  return (
    <div className={styles.syncedTransactionsWrapper}>
      <div className={styles.syncedTransactionsTop}>
        <div className={styles.syncedTransactionsTitle}>
          Linked Transactions
        </div>
      </div>
      {content}
    </div>
  );
}
